const clockFormatter = new Intl.DateTimeFormat("es-CO", {
  hour: "2-digit",
  minute: "2-digit",
});

const dayTimeFormatter = new Intl.DateTimeFormat("es-CO", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const dayFormatter = new Intl.DateTimeFormat("es-CO", {
  day: "numeric",
  month: "short",
});

const relativeFormatter = new Intl.RelativeTimeFormat("es", { numeric: "auto", style: "short" });

const RELATIVE_UNITS: readonly { limit: number; unit: Intl.RelativeTimeFormatUnit; ms: number }[] = [
  { limit: 60_000, unit: "second", ms: 1000 },
  { limit: 3_600_000, unit: "minute", ms: 60_000 },
  { limit: 86_400_000, unit: "hour", ms: 3_600_000 },
  { limit: 604_800_000, unit: "day", ms: 86_400_000 },
];

function parse(value: string | number): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatClock(iso: string): string {
  const date = parse(iso);
  return date === null ? "—" : clockFormatter.format(date);
}

export function formatDayTime(iso: string): string {
  const date = parse(iso);
  return date === null ? "—" : dayTimeFormatter.format(date);
}

/** Hora relativa en español: "hace 2 min", "hace 3 h". Para más de una semana, fecha corta. */
export function formatRelative(iso: string, now: number = Date.now()): string {
  const date = parse(iso);
  if (date === null) return "—";
  const diff = date.getTime() - now;
  const absolute = Math.abs(diff);
  if (absolute < 45_000) return diff < 0 ? "hace unos segundos" : "en unos segundos";
  for (const { limit, unit, ms } of RELATIVE_UNITS) {
    if (absolute < limit) return relativeFormatter.format(Math.round(diff / ms), unit);
  }
  return dayFormatter.format(date);
}

export function isToday(iso: string): boolean {
  const date = parse(iso);
  if (date === null) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export function formatEtaMinutes(seconds: number | undefined): string {
  if (seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return "menos de 1 min";
  return `${Math.round(seconds / 60)} min`;
}

export function formatEtaDetailed(seconds: number | undefined): string {
  if (seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  return `${hours} h ${totalMinutes % 60} min`;
}

export function formatDistanceKm(meters: number | undefined): string {
  if (meters === undefined || !Number.isFinite(meters) || meters < 0) return "—";
  if (meters < 950) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

export function formatDistanceDetailed(meters: number | undefined): string {
  if (meters === undefined || !Number.isFinite(meters) || meters < 0) return "—";
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}

export function formatSpeed(speedMps: number | undefined): string {
  if (speedMps === undefined || !Number.isFinite(speedMps) || speedMps < 0) return "—";
  return `${(speedMps * 3.6).toFixed(0)} km/h`;
}

/** "en 7 h 55 min" (para componer "Vence en 7 h 55 min"). */
export function formatCountdown(targetIso: string, now: number = Date.now()): string {
  const date = parse(targetIso);
  if (date === null) return "en un momento";
  const remaining = date.getTime() - now;
  if (remaining <= 0) return "ya venció";
  const minutes = Math.floor(remaining / 60_000);
  if (minutes < 1) return "en menos de 1 min";
  if (minutes < 60) return `en ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `en ${hours} h ${minutes % 60} min`;
}

export function isExpired(targetIso: string, now: number = Date.now()): boolean {
  const date = parse(targetIso);
  return date === null || date.getTime() - now <= 0;
}

const countFormatter = new Intl.NumberFormat("es-CO");

/** Números con separador de miles local: 1.240. */
export function formatCount(value: number): string {
  return Number.isFinite(value) ? countFormatter.format(value) : "—";
}

export function channelLabel(channel: string): string {
  switch (channel) {
    case "push":
      return "App";
    case "whatsapp":
      return "WhatsApp";
    case "call":
      return "Llamada";
    default:
      return channel;
  }
}

export function leadLabel(minutes: number): string {
  return minutes === 1 ? "1 min antes" : `${minutes} min antes`;
}
